/*
 * File: product-toolbar.component.ts                                          *
 * Project: catalog-cloud                                                      *
 * File Created: Wednesday, 12th January 2022 12:08:27                         *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Friday, 2nd December 2022 14:56:24                           *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { Catalog, Product } from '@nousmedis/cc-types';
import { ProductService } from '../../../services/product.service';
import { keyValue } from '../../../../tms-tools/interfaces/key-value';
import { UserService } from 'app/modules/user/services/user.service';
import { CookieService } from 'ngx-cookie-service';
import { FilterOptions } from 'app/modules/catalog-cloud/interfaces/filter-options.interface';
import { environment } from 'environment';
import { ItemDropInterface } from 'app/modules/catalog-cloud/interfaces/item-drop.interface';
import { Fascicle } from '@nousmedis/cc-types';
import { FascicleService } from 'app/modules/catalog-cloud/services/fascicle.service';
import { GridLocked } from 'app/modules/catalog-cloud/interfaces/grid-locked.interface';
import { CatalogService } from 'app/modules/catalog-cloud/services/catalog.service';

interface SortableField {
  label: string;
  value: string;
  group: 'standard' | 'extra';
}

@Component({
  selector: 'app-product-toolbar',
  templateUrl: './product-toolbar.component.html',
  styleUrls: ['./product-toolbar.component.scss'],
})
export class ProductToolbarComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('productDataList') scrollContainer!: ElementRef;
  @ViewChild('detailsContainer') detailsContainer!: ElementRef;

  @Input() idCatalog: string;
  @Input() idFascicle: string;
  @Input() filter: keyValue[];
  @Input() filterOptions: FilterOptions;

  @Output() showTooltip: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showHistory: EventEmitter<void> = new EventEmitter<void>();

  loading: boolean = true;
  viewType: string = 'list';
  catalog: Catalog;
  products: Product[] = [];
  filteredProducts: Product[] = [];
  filteredProductsOriginal: Product[] = [];
  addedProducts: string[] = [];

  orderObserver: Subscription;

  showScrollToTop: boolean = false;
  // showTooltip: boolean = false;
  productDataId = 'product-data';
  currentScroll = 0;

  itemDropSubscription: Subscription = new Subscription();
  filteredListSubscription: Subscription = new Subscription();

  lazyLoading: boolean = true;
  lazyListSubscription: Subscription = new Subscription();
  viewedProductsObserver: Subscription;
  viewedProducts: number = 0;

  refreshProductsObserver: Subscription;

  addProductToPageObserver: Subscription;
  removeProductToPageObserver: Subscription;
  lockedPageObserver: Subscription;

  productsInFascicle: string[] = [];

  originalHeight: number;
  counterClickUp: number = 0;
  counterClickDown: number = 2;

  /**
   * Variables para suffix de la cookie y id de usuario
   */

  cookieSuffix: string = '-product-toolbar';
  userId: string;
  cookieName: string;
  filterSettings: FilterOptions;
  filterLoaded: boolean = false;
  fascicle: Fascicle;
  showItemDetails: boolean;
  showFloatingDetails: boolean = false;
  isDragging: boolean = false;

  toolbarEnabled: boolean = true;
  sortableFields: SortableField[] = [];

  constructor (
    private productService: ProductService,
    private userService: UserService,
    private cookieService: CookieService,
    private fascicleService: FascicleService,
    private catalogService: CatalogService
  ) { } //Implementar el servicio de usuario y cookie

  ngAfterViewInit(): void {

    if (this.originalHeight) {
      this.originalHeight = this.detailsContainer.nativeElement.offsetHeight;
    }

    // Escuchar el evento de scroll
    this.scrollContainer.nativeElement.addEventListener('scroll', () =>
      this.onScroll()
    );
    // Garantizar que currentSearch.idCatalog tiene el valor correcto del Input
    // (puede llegar vacío en ngOnInit si se navega programáticamente)
    this.productService.setSearch(
      this.idCatalog,
      this.filter ?? [],
      [],
      this.filterSettings?.sort ?? '',
      'AND'
    );
    this.findProducts();

    //por defecto el boton de bajar estará oculto
    let downButton = document.getElementById('downButton');
    if (downButton && this.counterClickDown === 2) {
      downButton.style.display = 'none';
    }
  }

  onScroll(): void {
    const container = this.scrollContainer.nativeElement;
    const scrollPosition = container.scrollTop + container.clientHeight + 100; // 100px de margen para cargar antes
    const scrollHeight = container.scrollHeight;

    //Solo se realiza la carga si la posición del scroll es mayor o igual a la altura del scroll y el número de productos vistos es menor que el total de productos
    if (
      this.filteredProductsOriginal.length < this.viewedProducts &&
      scrollPosition >= scrollHeight
    ) {
      this.lazyLoading = true;
      this.productService.lazyLoadSearchProducts(
        this.fascicle.tags,
        this.fascicle.categories
      );
      this.productService.offsetSearchIncrement();
    }
  }


  findProducts() {
    firstValueFrom(
      this.productService.getAddedProducts(this.idCatalog, this.idFascicle)
    ).then(async (products) => {
      this.productsInFascicle = products;
      this.fascicle = await firstValueFrom(
        this.fascicleService.get(this.idCatalog, this.idFascicle)
      );
      this.productService.postSearchProducts(
        this.fascicle.tags,
        this.fascicle.categories
      );
    });
  }

  ngOnDestroy(): void {
    this.orderObserver.unsubscribe();
    this.viewedProductsObserver.unsubscribe();
    this.filteredListSubscription.unsubscribe();
    this.itemDropSubscription.unsubscribe();
    this.addProductToPageObserver.unsubscribe();
    this.removeProductToPageObserver.unsubscribe();
    this.lockedPageObserver.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.productService.isShowProductDetails().subscribe((show) => {
      this.showTooltip.emit(show);
    });

    /**
     *Inicializar el id del usuario y el nombre de la cookie
     */

    this.initSubscriptions();
    this.userId = this.userService.getCurrentUser().id;
    this.cookieName = this.userId + this.cookieSuffix;
    this.initFilterOptions();
    /**
     * Inicializar la cookie
     */
    this.productService.setShowProductsDetails(this.filterSettings.showWindow);

    this.catalog = await firstValueFrom(this.catalogService.getCatalog(this.idCatalog));
    this.viewType = this.filterSettings.viewType;
    this.productService.setSearch(
      this.idCatalog,
      this.filter,
      [],
      this.filterSettings.sort,
      'AND'
    );
  }

  initSubscriptions() {
    this.lockedPageObserver = this.fascicleService.lockButtons.subscribe((gridLocked: GridLocked) => {
      this.toolbarEnabled = gridLocked.locked;
    });

    this.refreshProductsObserver = this.productService.refreshProducts$.subscribe(() => {
      this.findProducts();
      this.refreshList(this.filteredProductsOriginal);
    });

    this.addProductToPageObserver = this.productService.addProduct$.subscribe(
      (product) => {
        this.productsInFascicle.push(product);
        this.refreshList(this.filteredProductsOriginal);
      }
    );

    this.removeProductToPageObserver =
      this.productService.removeProduct$.subscribe((product) => {
        this.productsInFascicle.indexOf(product) !== -1
          ? this.productsInFascicle.splice(
            this.productsInFascicle.indexOf(product),
            1
          )
          : null;
        this.refreshList(this.filteredProductsOriginal);
      });

    this.orderObserver = this.productService.orderHandler.subscribe((order) => {
      this.changeOrder(order);
    });

    this.viewedProductsObserver =
      this.productService.totalProductList$.subscribe((total) => {
        this.viewedProducts = total;
      });

    this.filteredListSubscription = this.productService.productList$.subscribe(
      (products) => {
        this.products = products;
        this.refreshList(products);
      }
    );

    this.lazyListSubscription = this.productService.lazyLoadProducts$.subscribe(
      (products) => {
        this.addToList(products);
      }
    );
  }

  refreshList(productList: Product[]) {
    this.loading = true;
    this.filteredProducts = productList ? productList : [];
    this.filteredProductsOriginal = productList
      ? JSON.parse(JSON.stringify(productList))
      : [];

    if (!this.filterSettings.viewInsertedProducts) {
      this.filteredProducts = this.filteredProducts.filter(
        (product) => !this.productsInFascicle.includes(product.id)
      );
    }
    this.buildSortableFields(this.filteredProducts);
    this.lazyLoading = false;
    this.loading = false;
  }

  private buildSortableFields(products: Product[]): void {
    const standard: SortableField[] = [
      { label: 'Nombre', value: 'name', group: 'standard' },
      { label: 'SKU', value: 'sku', group: 'standard' },
      { label: 'Familia', value: 'family', group: 'standard' },
      { label: 'Descripción', value: 'description', group: 'standard' },
    ];

    // Para extraFields: solo incluir keys cuyo valor sea escalar (string/number)
    // en el primer producto que lo tenga. Arrays y objetos se excluyen.
    const extraKeys = new Set<string>();
    products.forEach(p => {
      if (p.extraFields && typeof p.extraFields === 'object') {
        Object.entries(p.extraFields).forEach(([k, v]) => {
          if (!Array.isArray(v) && v !== null && typeof v !== 'object') {
            extraKeys.add(k);
          }
        });
      }
    });

    const extra: SortableField[] = [...extraKeys].map(k => ({
      label: k,
      value: `extraFields.${k}`,
      group: 'extra' as const,
    }));

    this.sortableFields = [...standard, ...extra];
  }

  addToList(productList: Product[]) {
    this.filteredProducts.push(...productList);
    this.filteredProductsOriginal.push(
      ...JSON.parse(JSON.stringify(productList))
    );
    this.lazyLoading = false;
  }

  changeViewType(type: string) {
    this.viewType = type;
  }

  changeOrder(order: string) {
    const parts = order.includes(':') ? order.split(':') : ['name', order];
    const fieldPath = parts[0];
    const direction = parts[1] as 'asc' | 'desc';

    this.filteredProducts = [...this.filteredProducts].sort((a, b) => {
      let valA: string;
      let valB: string;

      if (fieldPath.startsWith('extraFields.')) {
        const key = fieldPath.replace('extraFields.', '');
        valA = String((a.extraFields as any)?.[key] ?? '');
        valB = String((b.extraFields as any)?.[key] ?? '');
      } else {
        valA = String((a as any)[fieldPath] ?? '');
        valB = String((b as any)[fieldPath] ?? '');
      }

      return direction === 'asc'
        ? valA.localeCompare(valB, undefined, { numeric: true })
        : valB.localeCompare(valA, undefined, { numeric: true });
    });
  }

  scrollToTop() {
    document.getElementById(this.productDataId).scrollTop = 0;
  }

  /**
   * Funcion para recoger la cookie y si no existe, inicializarla
   */

  initFilterCookie() {
    if (this.cookieService.check(this.cookieName)) {
      this.filterSettings = JSON.parse(this.cookieService.get(this.cookieName));
    } else {
      this.cookieService.set(
        this.cookieName,
        JSON.stringify(this.filterSettings),
        365,
        '/',
        environment.base_domain,
        environment.production
      );
    }
  }

  /**
   * Función para iniciar los atributos por defecto de la interfaz filter-options
   */
  initFilterOptions() {
    this.filterSettings = {
      searchType: 'OR',
      viewInsertedProducts: false,
      showWindow: false,
      sort: 'asc',
      sortField: 'name',
      viewType: 'list',
    };
    this.initFilterCookie();
    this.showItemDetails = this.filterSettings.showWindow;
    this.filterLoaded = true;
  }

  /**
   * Get del sendFilter
   */

  getFilterOptions(event: FilterOptions) {
    this.cookieService.set(
      this.cookieName,
      JSON.stringify(event),
      365,
      '/',
      environment.base_domain,
      environment.production
    );

    //this.refreshList(this.filteredProductsOriginal);
    this.showItemDetails = this.filterSettings.showWindow;
  }

  resizeDiv(direction: string): void {
    if (!this.originalHeight) {
      this.originalHeight = this.detailsContainer.nativeElement.offsetHeight;
    }

    if (direction === 'up' && this.counterClickUp < 2) {
      this.counterClickUp++;
      this.counterClickDown = Math.max(0, this.counterClickDown - 1);
      const factor = this.counterClickUp === 1 ? 1.5 : 2;
      this.detailsContainer.nativeElement.style.height = `${this.originalHeight * factor}px`;
    }

    if (direction === 'down' && this.counterClickDown < 2) {
      this.counterClickDown++;
      this.counterClickUp = Math.max(0, this.counterClickUp - 1);
      const factor = this.counterClickDown === 1 ? 1.5 : 1;
      this.detailsContainer.nativeElement.style.height = `${this.originalHeight * factor}px`;
    }
  }

  closeDetails() {
    console.log("Btn pulsado");

    // Cambiar el estado global a false
    this.productService.setShowProductsDetails(false);

    // Actualizar el estado local de filterSettings
    if (this.filterSettings) {
      this.filterSettings.showWindow = false;
    }

    // Emitir los cambios
    this.getFilterOptions(this.filterSettings);
  }

  drop(event: any) {
    const idDroppedProduct = this.productService.getDroppedProductToList();
    this.productService.dropToListHandler.next(idDroppedProduct);
    this.isDragging = false;
  }

  OnDragEnter(): void {
    this.isDragging = true;
  }

  OnDragLeave(): void {
    this.isDragging = false;

  }
}
